/**
 * MSW Mocking Entry Point
 *
 * This module provides the `enableMocking()` function that should be called
 * at application startup to enable API mocking in test or MSW mode.
 *
 * Usage: Call with await inside an IIFE within onMount to ensure MSW is ready
 * before any API calls are made. See +layout.svelte for the full implementation.
 */

// Guard to prevent multiple initializations
let mswStarted = false;
let mswStartPromise: Promise<void> | null = null;

/**
 * Enable MSW mocking if running in test or MSW mode
 *
 * This function:
 * - Checks if the app is running in test mode (MODE=test) or MSW mode (MODE=msw)
 * - Dynamically imports the MSW browser worker
 * - Starts the service worker to intercept requests
 * - Is idempotent - safe to call multiple times
 *
 * @returns Promise that resolves when mocking is enabled (or immediately if not in mock mode)
 */
export async function enableMocking(): Promise<void> {
	const mode = import.meta.env.MODE;

	// Only enable mocking in test or msw mode
	if (mode !== 'test' && mode !== 'msw') {
		return;
	}

	// If already started, return immediately
	if (mswStarted) {
		return;
	}

	// If currently starting, wait for it to complete
	if (mswStartPromise) {
		return mswStartPromise;
	}

	// Start MSW
	mswStartPromise = (async () => {
		console.log(`[MSW] Enabling API mocking for ${mode} mode...`);

		const { worker } = await import('./browser');

		await worker.start({
			// Let unhandled requests pass through (static assets, etc.)
			onUnhandledRequest: 'bypass',
			// Quiet mode - don't log every intercepted request
			quiet: false
		});

		mswStarted = true;
		console.log('[MSW] Mocking enabled');
	})();

	return mswStartPromise;
}

// Re-export test user data for use in tests
export { TEST_USER, MOCK_TOKEN } from './handlers/kinde.handler';
